import {
  type CreateOrderPayload,
  type Order,
  type OrderSearchFilters,
  type UpdateOrderPayload,
  mapOrderResponse,
  mapOrdersResponse,
} from "../types/order";
import {
  type PaginationParam,
  type PaginationResponse,
  mapPaginationResponse,
} from "../types/paginationResponse";
import { buildPaginationQuery } from "../utils/pagination";
import api from "./axios";

export interface SearchOrdersResponse {
  orders: Order[];
  pagination: PaginationResponse;
}

export const searchOrdersApi = async (
  filters?: OrderSearchFilters,
  pagination?: PaginationParam,
): Promise<SearchOrdersResponse> => {
  try {
    const queryString = buildPaginationQuery(pagination);
    const url = `/api/v1/orders/search${queryString}`;
    const response = await api.post<any>(url, filters ?? {});
    return {
      orders: mapOrdersResponse(response.data),
      pagination: mapPaginationResponse(response.data.data.pagination),
    };
  } catch (error) {
    console.error("Error searching orders:", error);
    throw error;
  }
};

export const createOrderApi = async (data: CreateOrderPayload): Promise<Order> => {
  try {
    const response = await api.post<any>("/api/v1/orders", data);
    return mapOrderResponse(response.data.data.order);
  } catch (error) {
    console.error("Error creating order:", error);
    throw error;
  }
};

export const getOrderByIdApi = async (orderId: string): Promise<Order> => {
  try {
    const response = await api.get<any>(`/api/v1/orders/${orderId}`);
    return mapOrderResponse(response.data.data.order);
  } catch (error) {
    console.error("Error fetching order:", error);
    throw error;
  }
};

export const updateOrderApi = async (orderId: string, data: UpdateOrderPayload): Promise<Order> => {
  try {
    const response = await api.put<any>(`/api/v1/orders/${orderId}`, data);
    return mapOrderResponse(response.data.data.order);
  } catch (error) {
    console.error("Error updating order:", error);
    throw error;
  }
};

export const readyForReviewOrderApi = async (orderId: string): Promise<Order> => {
  try {
    const response = await api.post<any>(`/api/v1/orders/${orderId}/readyForReview`, {});
    return mapOrderResponse(response.data.data.order);
  } catch (error) {
    console.error("Error marking order ready for review:", error);
    throw error;
  }
};

export const reopenOrderApi = async (orderId: string): Promise<Order> => {
  try {
    const response = await api.post<any>(`/api/v1/orders/${orderId}/reopen`, {});
    return mapOrderResponse(response.data.data.order);
  } catch (error) {
    console.error("Error reopening order:", error);
    throw error;
  }
};

export const deleteOrderApi = async (orderId: string): Promise<void> => {
  try {
    await api.delete(`/api/v1/orders/${orderId}`);
  } catch (error) {
    console.error("Error deleting order:", error);
    throw error;
  }
};

export const requestRevisionOrderApi = async (orderId: string, note: string): Promise<Order> => {
  try {
    const response = await api.post<any>(`/api/v1/orders/${orderId}/requestRevision`, {
      note,
    });
    return mapOrderResponse(response.data.data.order);
  } catch (error) {
    console.error("Error requesting order revision:", error);
    throw error;
  }
};

export const updateExpenseReviewChecklistApi = async (
  orderId: string,
  key: string,
  checked: boolean,
): Promise<Order> => {
  try {
    const response = await api.put<any>(`/api/v1/orders/${orderId}/expenseReviewChecklist`, {
      key,
      checked,
    });
    return mapOrderResponse(response.data.data.order);
  } catch (error) {
    console.error("Error updating expense review checklist:", error);
    throw error;
  }
};

export const verifyOrderApi = async (orderId: string): Promise<Order> => {
  try {
    const response = await api.post<any>(`/api/v1/orders/${orderId}/verify`, {});
    return mapOrderResponse(response.data.data.order);
  } catch (error) {
    console.error("Error verifying order:", error);
    throw error;
  }
};

export const submitRevisionOrderApi = async (orderId: string, note?: string): Promise<Order> => {
  try {
    const response = await api.post<any>(
      `/api/v1/orders/${orderId}/submitRevision`,
      note ? { note } : {},
    );
    return mapOrderResponse(response.data.data.order);
  } catch (error) {
    console.error("Error submitting order revision:", error);
    throw error;
  }
};

export const approveOrderApi = async (orderId: string): Promise<Order> => {
  try {
    const response = await api.post<any>(`/api/v1/orders/${orderId}/approve`, {});
    return mapOrderResponse(response.data.data.order);
  } catch (error) {
    console.error("Error approving order:", error);
    throw error;
  }
};

export interface ApproveOrderWithOverridePayload {
  expenseOverrides?: Array<{ id: string; overrideAmount: number | null }>;
  overrideNotes?: string;
}

export const getTempOrderProofUploadUrlApi = async (
  contentType: string,
  filename: string,
): Promise<{ uploadUrl: string; key: string; maxBytes: number }> => {
  const response = await api.post<{
    data: { uploadUrl: string; key: string; maxBytes: number };
  }>("/api/v1/orders/proof/tmp/upload-url", { contentType, filename });
  return response.data.data;
};

export const confirmTempOrderProofApi = async (
  key: string,
): Promise<{ key: string; filename: string }> => {
  const response = await api.post<{ data: { key: string; filename: string } }>(
    "/api/v1/orders/proof/tmp/confirm",
    { key },
  );
  return response.data.data;
};

export const uploadOrderProofToTemp = async (
  file: File,
): Promise<{ key: string; filename: string }> => {
  const maxBytes = 5 * 1024 * 1024;
  if (file.size > maxBytes) {
    throw new Error("ORDER_PROOF_TOO_LARGE");
  }
  const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
  if (!allowedTypes.includes(file.type)) {
    throw new Error("ORDER_PROOF_INVALID_TYPE");
  }

  const { uploadUrl, key } = await getTempOrderProofUploadUrlApi(file.type, file.name);
  const putResponse = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });
  if (!putResponse.ok) {
    throw new Error("ORDER_PROOF_UPLOAD_FAILED");
  }
  return confirmTempOrderProofApi(key);
};

export const approveOrderWithOverrideApi = async (
  orderId: string,
  payload: ApproveOrderWithOverridePayload,
): Promise<Order> => {
  try {
    const response = await api.post<any>(`/api/v1/orders/${orderId}/approveWithOverride`, payload);
    return mapOrderResponse(response.data.data.order);
  } catch (error) {
    console.error("Error approving order with override:", error);
    throw error;
  }
};
